/**
 * Year6BookPreviewModal — composite "memory book" preview for parents.
 *
 * Renders 4 virtual pages composed entirely from existing Parent View
 * data (no PDF generation). Page 1 is sharp ("hook"); pages 2-4 are
 * progressively blurred with a lock overlay → £99 unlock CTA.
 *
 * Psychology stack:
 *   Curiosity → Recognition (real photos/messages) → Tension (blur)
 *   → Loss aversion → Frictionless unlock.
 *
 * After purchase, blurred pages animate to sharp in-place (300ms) before
 * the parent is redirected to /success.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, CaretLeft, CaretRight, LockKey, Check, GraduationCap, Heart,
  CircleNotch, Sparkle,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Year6BookPreviewModal({
  open,
  onClose,
  data,           // parent-view-data
  slug,
  onUnlock,       // () => Promise — calls Stripe checkout
  unlocking,      // boolean
  unlocked,       // boolean — true after purchase
}) {
  const [pageIdx, setPageIdx] = useState(0);
  const [revealing, setRevealing] = useState(false);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Reset to page 1 each open
  useEffect(() => { if (open) setPageIdx(0); }, [open]);

  // When `unlocked` flips true while modal is open, run the reveal anim
  useEffect(() => {
    if (!open) return;
    if (unlocked) {
      setRevealing(true);
      const t = setTimeout(() => setRevealing(false), 1200);
      return () => clearTimeout(t);
    }
  }, [unlocked, open]);

  if (!open || !data) return null;

  const photos = (data.photo_urls || []).slice(0, 18);
  const messages = (data.messages || []).slice(0, 6);
  const schoolName = data.school_name || data.event_name || 'Year 6';
  const totalPhotos = data.total_photos || photos.length;
  const totalMessages = data.total_messages || messages.length;

  const pages = buildPages({ schoolName, photos, messages, totalPhotos, totalMessages });
  const totalPages = pages.length;
  const goPrev = () => setPageIdx((i) => Math.max(0, i - 1));
  const goNext = () => setPageIdx((i) => Math.min(totalPages - 1, i + 1));

  // Sharp on page 0 always; if purchased, all sharp; while revealing, fade to sharp
  const isPageSharp = (idx) => idx === 0 || unlocked || revealing;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] flex items-stretch sm:items-center justify-center bg-black/82 backdrop-blur-md p-0 sm:p-6"
        onClick={onClose}
        data-testid="year6-book-preview-backdrop"
      >
        <motion.div
          initial={{ y: 30, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 30, opacity: 0 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full sm:max-w-[480px] h-full sm:h-auto sm:max-h-[92vh] flex flex-col bg-[#0F1014] sm:rounded-3xl shadow-2xl border border-white/10 overflow-hidden"
          data-testid="year6-book-preview-modal"
        >
          {/* Header */}
          <div className="shrink-0 flex items-center justify-between px-5 py-3.5 border-b border-white/8">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-200/80 inline-flex items-center gap-1.5">
                <GraduationCap size={11} weight="fill" /> Memory Book Preview
              </p>
              <p className="text-white text-[15px] font-bold leading-tight" data-testid="year6-book-preview-title">
                {unlocked ? 'Your Memory Book' : "See what's been created"}
              </p>
            </div>
            <button onClick={onClose} aria-label="Close" className="p-2 rounded-full hover:bg-white/8 text-white/60 hover:text-white" data-testid="year6-book-preview-close">
              <X size={18} weight="bold" />
            </button>
          </div>

          {/* Page viewer */}
          <div className="flex-1 overflow-hidden relative bg-[#06070f]">
            <div className="h-full flex items-center justify-center p-4 sm:p-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={pageIdx}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.28 }}
                  className="relative w-full max-w-[360px] aspect-[3/4] rounded-2xl shadow-2xl overflow-hidden"
                  data-testid={`year6-book-preview-page-${pageIdx}`}
                >
                  <BookPage
                    page={pages[pageIdx]}
                    sharp={isPageSharp(pageIdx)}
                    revealing={revealing}
                    pageNumber={pageIdx + 1}
                  />

                  {/* Lock overlay only when not sharp & this isn't the cover */}
                  {!isPageSharp(pageIdx) && (
                    <PageLockOverlay
                      onUnlock={onUnlock}
                      unlocking={unlocking}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Carousel arrows */}
            {pageIdx > 0 && (
              <button
                onClick={goPrev}
                aria-label="Previous page"
                className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/8 hover:bg-white/15 backdrop-blur text-white flex items-center justify-center"
                data-testid="year6-book-preview-prev"
              >
                <CaretLeft size={18} weight="bold" />
              </button>
            )}
            {pageIdx < totalPages - 1 && (
              <button
                onClick={goNext}
                aria-label="Next page"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/8 hover:bg-white/15 backdrop-blur text-white flex items-center justify-center"
                data-testid="year6-book-preview-next"
              >
                <CaretRight size={18} weight="bold" />
              </button>
            )}
          </div>

          {/* Footer */}
          <div className="shrink-0 border-t border-white/8 px-5 py-3 bg-[#0F1014]">
            {/* Page indicator */}
            <div className="flex items-center justify-center gap-1.5 mb-2.5" data-testid="year6-book-preview-pager">
              {pages.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${i === pageIdx ? 'w-6 bg-amber-300' : 'w-1.5 bg-white/20'}`}
                />
              ))}
            </div>

            {unlocked ? (
              <a
                href={`/year6/${slug}/success`}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-sm font-black text-white shadow-lg"
                style={{ background: 'linear-gradient(135deg, #22C55E, #10B981)' }}
                data-testid="year6-book-preview-open-book"
              >
                <Check size={16} weight="bold" /> Open Memory Book
              </a>
            ) : (
              <>
                <button
                  onClick={onUnlock}
                  disabled={unlocking}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-sm font-black text-white shadow-lg disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
                  data-testid="year6-book-preview-unlock"
                >
                  {unlocking ? (
                    <><CircleNotch size={14} weight="bold" className="animate-spin" /> Preparing your Memory Book…</>
                  ) : (
                    <><LockKey size={14} weight="fill" /> Unlock the full Memory Book — £99</>
                  )}
                </button>
                <p className="text-center text-[11px] text-white/45 mt-2" data-testid="year6-book-preview-microcopy">
                  Instant download • One-time payment
                </p>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ---------------------- PAGE BUILDER ---------------------- */
function buildPages({ schoolName, photos, messages, totalPhotos, totalMessages }) {
  const pages = [];

  // PAGE 1 — COVER (sharp)
  pages.push({
    type: 'cover',
    title: schoolName,
    photos: photos.slice(0, 5),
  });

  // PAGE 2 — MEMORY MESSAGES (blurred — but text peeks through for emotion)
  pages.push({
    type: 'messages',
    title: 'In their own words',
    messages: messages.slice(0, 4),
    totalMessages,
  });

  // PAGE 3 — CLASS MOMENTS GRID (blurred)
  pages.push({
    type: 'photo_grid',
    title: 'Class moments',
    photos: photos.slice(5, 13),
    totalPhotos,
  });

  // PAGE 4 — LAST TIMES / CLOSING (blurred)
  pages.push({
    type: 'closing',
    title: 'Last times',
    photos: photos.slice(13, 17),
  });

  return pages;
}

/* ---------------------- BOOK PAGE TEMPLATES ---------------------- */
function BookPage({ page, sharp, revealing, pageNumber }) {
  const blurStyle = sharp
    ? { filter: 'blur(0px)', transform: 'scale(1)', transition: 'filter 480ms ease, transform 480ms ease' }
    : { filter: 'blur(13px)', transform: 'scale(1.02)' };

  return (
    <div className="absolute inset-0 bg-white text-[#0F1014] flex flex-col" style={{ ...blurStyle }}>
      {/* Subtle paper grain */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #000 1px, transparent 1px), radial-gradient(circle at 70% 60%, #000 1px, transparent 1px)', backgroundSize: '4px 4px, 6px 6px' }} />

      {revealing && (
        <div className="absolute inset-0 pointer-events-none animate-pulse" style={{ background: 'radial-gradient(circle at center, rgba(250,204,21,0.18), transparent 60%)' }} />
      )}

      {/* PAGE CONTENT */}
      {page.type === 'cover' && <CoverPage page={page} />}
      {page.type === 'messages' && <MessagesPage page={page} />}
      {page.type === 'photo_grid' && <PhotoGridPage page={page} />}
      {page.type === 'closing' && <ClosingPage page={page} />}

      {/* Footer */}
      <div className="shrink-0 px-5 py-2.5 flex items-center justify-between text-[9px] uppercase tracking-[0.18em] text-neutral-500 font-bold border-t border-neutral-200/80">
        <span>Year 6 · 2026</span>
        <span>Page {pageNumber}</span>
      </div>
    </div>
  );
}

function CoverPage({ page }) {
  return (
    <div className="flex-1 relative overflow-hidden">
      {/* Hero photo */}
      {page.photos[0] ? (
        <img src={resolveUrl(page.photos[0])} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #EBF4FF 0%, #F3E8FF 60%, #FEF3C7 100%)' }} />
      )}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.85) 100%)' }} />

      <div className="relative h-full flex flex-col justify-end p-6 text-white">
        <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/20 backdrop-blur border border-white/30 text-[9px] font-black uppercase tracking-[0.18em] self-start mb-3">
          <GraduationCap size={10} weight="fill" /> Year 6 Leavers
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl font-black leading-[1.05] mb-1" style={{ fontFamily: 'Georgia, "Playfair Display", serif' }}>
          {page.title}
        </h2>
        <p className="text-white/85 text-[13px] font-semibold mb-3">Memory Book · Class of 2026</p>

        {/* Mini photo strip — 4 thumbs */}
        {page.photos.length > 1 && (
          <div className="grid grid-cols-4 gap-1 mt-2">
            {page.photos.slice(1, 5).map((url, i) => (
              <div key={i} className="aspect-square rounded-md overflow-hidden border border-white/30">
                <img src={resolveUrl(url)} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )}
        <p className="text-white/70 text-[10px] italic mt-3">Created together by your class</p>
      </div>
    </div>
  );
}

function MessagesPage({ page }) {
  return (
    <div className="flex-1 px-6 pt-6 pb-3 overflow-hidden">
      <div className="text-center mb-4">
        <Heart size={16} weight="fill" className="inline text-rose-400 mb-1" />
        <h3 className="font-serif text-2xl font-black text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>{page.title}</h3>
        <p className="text-neutral-500 text-[10px] uppercase tracking-[0.18em] font-black mt-0.5">{page.totalMessages || page.messages.length} messages from the class</p>
      </div>
      <div className="space-y-2.5">
        {page.messages.map((m, i) => (
          <div key={i} className={`rounded-xl px-3.5 py-2.5 ${i % 2 === 0 ? 'bg-rose-50' : 'bg-blue-50'} border border-neutral-200/60`}>
            <p className="text-neutral-800 text-[12.5px] leading-snug font-medium" style={{ fontFamily: 'Georgia, serif' }}>
              "{(m.text || '').slice(0, 110)}{(m.text || '').length > 110 ? '…' : ''}"
            </p>
            {m.author && <p className="text-neutral-500 text-[10px] mt-1 italic">— {m.author}</p>}
          </div>
        ))}
        {page.messages.length === 0 && (
          <>
            <PlaceholderMessage text="I'll miss everyone so much…" tone="rose" />
            <PlaceholderMessage text="Best year ever 💛" tone="blue" />
            <PlaceholderMessage text="Thank you for everything" tone="rose" />
          </>
        )}
      </div>
    </div>
  );
}

function PlaceholderMessage({ text, tone }) {
  const bg = tone === 'rose' ? 'bg-rose-50' : 'bg-blue-50';
  return (
    <div className={`rounded-xl px-3.5 py-2.5 ${bg} border border-neutral-200/60`}>
      <p className="text-neutral-800 text-[12.5px] leading-snug font-medium" style={{ fontFamily: 'Georgia, serif' }}>"{text}"</p>
    </div>
  );
}

function PhotoGridPage({ page }) {
  const photos = page.photos.length > 0 ? page.photos : new Array(8).fill(null);
  return (
    <div className="flex-1 px-5 pt-5 pb-3">
      <div className="text-center mb-3">
        <h3 className="font-serif text-2xl font-black text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>{page.title}</h3>
        <p className="text-neutral-500 text-[10px] uppercase tracking-[0.18em] font-black mt-0.5">{page.totalPhotos || photos.length} photos</p>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {photos.slice(0, 9).map((url, i) => (
          <div key={i} className={`overflow-hidden rounded-md ${i === 4 ? 'col-span-2 row-span-2 aspect-square' : 'aspect-square'} bg-neutral-100`}>
            {url ? (
              <img src={resolveUrl(url)} alt="" className="w-full h-full object-cover" />
            ) : null}
          </div>
        ))}
      </div>
      <p className="text-center text-neutral-400 text-[10px] italic mt-3">Best day ever</p>
    </div>
  );
}

function ClosingPage({ page }) {
  const photos = page.photos;
  return (
    <div className="flex-1 px-6 pt-6 pb-3 flex flex-col">
      <div className="text-center mb-4">
        <h3 className="font-serif text-2xl font-black text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>{page.title}</h3>
      </div>
      <div className="flex-1 space-y-2.5">
        {['Last day in the classroom', 'Last assembly', 'Walking out of the gates'].map((label, i) => (
          <div key={label} className="flex items-center gap-3">
            <div className="shrink-0 w-16 h-16 rounded-md overflow-hidden bg-neutral-100">
              {photos[i] ? <img src={resolveUrl(photos[i])} alt="" className="w-full h-full object-cover" /> : null}
            </div>
            <p className="text-neutral-700 text-[12.5px] font-semibold" style={{ fontFamily: 'Georgia, serif' }}>{label}</p>
          </div>
        ))}
      </div>
      <p className="text-center text-neutral-500 text-[11px] italic mt-3">Wherever you go next, remember this year.</p>
    </div>
  );
}

/* ---------------------- LOCK OVERLAY ---------------------- */
function PageLockOverlay({ onUnlock, unlocking }) {
  return (
    <div className="absolute inset-0 flex items-end justify-center p-4 pointer-events-none" data-testid="year6-book-preview-lock">
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.45) 70%, rgba(0,0,0,0.78) 100%)' }} />
      <div className="relative pointer-events-auto w-full max-w-[300px] rounded-2xl border border-amber-300/40 bg-[#0F1014]/92 backdrop-blur-sm p-4 text-center shadow-2xl">
        <div className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-amber-300/15 border border-amber-300/35 text-amber-300 mb-2">
          <LockKey size={16} weight="fill" />
        </div>
        <p className="text-white font-black text-[15px] leading-tight mb-1">Unlock the full Memory Book 🎓</p>
        <p className="text-white/60 text-[11.5px] leading-snug mb-3">See every page, photo and message</p>
        <button
          onClick={onUnlock}
          disabled={unlocking}
          className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-[13px] font-black text-white shadow-lg disabled:opacity-60"
          style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
          data-testid="year6-book-preview-lock-cta"
        >
          {unlocking ? (
            <><CircleNotch size={12} weight="bold" className="animate-spin" /> Opening checkout…</>
          ) : (
            <><Sparkle size={12} weight="fill" /> Unlock now — £99</>
          )}
        </button>
        <p className="text-white/45 text-[10px] mt-2">Instant download • One-time payment</p>
      </div>
    </div>
  );
}
